import { isString, noop } from "lodash-es";

import { webEvent } from "@/analytics/events/event";
import type { AnalyticsEvent, TrackingParams } from "@/analytics/types";
import { captureMessage } from "@/error/sentry";
import type { ActionSource } from "@/model/action";
import type { Key } from "@/utils/shortcut/key";
import { noKey } from "@/utils/shortcut/key";
import { toSnakeCase } from "@/utils/text/text";

import type {
  Action,
  ActionContext,
  ActionData,
  ActionDefinitionData,
  ActionSourceFun,
  ContextFun,
  Fun,
} from "./types";

const ignoreActions = ["board.clearCardSelection"];

let trackEvent: (event: AnalyticsEvent) => void = noop;

export function setActionTracker(tracker: (event: AnalyticsEvent) => void) {
  trackEvent = tracker;
}

/**
 * Create an action from its implementation and metadata.
 */
export function action<TParams extends unknown[], TReturn, TState extends { id: string }>(
  impl: Fun<TParams, TReturn>,
  data: ActionDefinitionData<TParams, TState> = {},
): Action<TParams, TReturn, TState> {
  const ctx = actionContext((_, ...params) => impl(...params), makeActionData(data));
  return makeAction(ctx.execute, ctx.data);
}

/**
 * Create an action that has ActionContext as first parameter.
 */
export function ctxAction<TParams extends unknown[], TReturn, TState extends { id: string }>(
  impl: ContextFun<TParams, TReturn>,
  data: ActionDefinitionData<TParams, TState> = {},
): Action<TParams, TReturn, TState> {
  const ctx = actionContext(impl, makeActionData(data));
  return makeAction(ctx.execute, ctx.data);
}

/**
 * Create an action that will never produce a tracking event.
 * This can happen either by calling ctx.track(null) or by using another action.
 */
export function untrackedAction<TParams extends unknown[], TReturn, TState extends { id: string }>(
  impl: ContextFun<TParams, TReturn>,
  data: ActionDefinitionData<TParams, TState> = {},
): Action<TParams, TReturn, TState> {
  const ctx = actionContext(impl, makeActionData(data), true);
  return makeAction(ctx.execute, ctx.data);
}

export function assignShortcut(action: Action, key: Key) {
  action.data.shortcut = key;
}

/**
 * Create an action without parameters from another action and parameters.
 */
export function applyActionParams<TParams extends unknown[], TReturn>(
  action: Action<TParams, TReturn>,
  ...params: TParams
): Action {
  return makeAction(
    (source: ActionSource | ActionContext) => action(source, ...params),
    action.data,
  );
}

/**
 * Define a group of actions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Actions have varying parameter types
export function defineActions<T extends Record<string, Action<any[], unknown>>>(
  name: string,
  actions: T,
): { [id in keyof T]: T[id] } {
  Object.entries(actions).forEach(([id, action]) => {
    action.data.id = name + "." + id;
  });
  return actions;
}

type InternalActionContext<TParams extends unknown[], TReturn, TState> = ActionContext & {
  params?: TParams;
  data: ActionData<TParams, TState>;
  execute: ActionSourceFun<TParams, TReturn>;
};

function actionContext<TParams extends unknown[], TReturn, TState extends { id: string }>(
  impl: ContextFun<TParams, TReturn>,
  data: ActionData<TParams, TState>,
  untracked?: boolean,
): InternalActionContext<TParams, TReturn, TState> {
  const context: InternalActionContext<TParams, TReturn, TState> = {
    tracked: false,
    data,
    source: "internal",
    track(arg: AnalyticsEvent | TrackingParams | null) {
      if (arg && this.source !== "internal" && !ignoreActions.includes(data.id)) {
        if (untracked && !isAnalyticsEvent(arg)) {
          captureMessage("An action marked as untracked triggered a tracking event", {
            action: { name: data.id },
          });
        }
        trackAction(data, this.source, this.params!, arg);
      }
      this.tracked = true;
    },
    execute(source: ActionSource | ActionContext, ...params: TParams) {
      context.source = isString(source) ? source : source.source;
      context.params = params;
      context.tracked = false;
      const res = impl(context, ...params);
      if (!context.tracked) {
        context.track({});
      }
      // an action calls another action with its context -> don't track the first action
      if (!isString(source)) {
        source.tracked = true;
      }
      return res;
    },
  };
  return context;
}

function makeAction<TParams extends unknown[], TReturn, TState>(
  fun: ActionSourceFun<TParams, TReturn>,
  data: ActionData<TParams, TState>,
): Action<TParams, TReturn, TState> {
  const res = fun as Action<TParams, TReturn, TState>;
  res.data = data;
  return res;
}

function makeActionData<TParams extends unknown[], TState>(
  data: ActionDefinitionData<TParams, TState>,
): ActionData<TParams, TState> {
  return {
    id: "",
    name: data.name || "",
    label: data.label || data.name || "",
    history: data.history,
    shortcut: noKey,
    icon: data.icon,
    autoToggle: data.autoToggle,
    disabled: data.disabled,
    active: data.active,
  };
}

const lastActionId: Record<string, string> = {};

function trackAction<TParams extends unknown[], TState extends { id: string }>(
  data: ActionData<TParams, TState>,
  source: ActionSource,
  params: TParams,
  track: AnalyticsEvent | TrackingParams,
) {
  if (data.history?.merge) {
    const id = data.history.saveState(...params).id;
    if (id !== lastActionId[data.id]) {
      lastActionId[data.id] = id;
      sendEvent();
    }
  } else {
    sendEvent();
  }

  function sendEvent() {
    trackEvent(isAnalyticsEvent(track) ? track : actionEvent(data.id, source, track));
  }
}

function actionEvent(eventName: string, source: ActionSource, params?: TrackingParams) {
  // Transform event name to snake case (action events are in dot.separated.camelCase)
  // eg. "board.workModeChanged" -> "board.work_mode_changed"
  return webEvent("action", toSnakeCase(eventName), { trigger: source, ...params });
}

function isAnalyticsEvent(arg: AnalyticsEvent | TrackingParams): arg is AnalyticsEvent {
  return (arg as AnalyticsEvent).event !== undefined;
}
